import formatters from 'lib/formatters'
import { httpGet } from 'lib/http'
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { IconRedirect } from '../../../../../components/Icons/IconRedirect'
import styles from './Header.css'
import AccordionStylingExpansion from 'components/Accordion/Accordion'
import RequestInfo from '../RequestInfo'
import FeeSchedule from '../FeeSchedule'
import Row from './Row'
import IconSwitch from 'components/IconSwitch/IconSwitch'
import { Notes } from 'components/Notes'

type Props = {
  number: string,
  beneficiary: string,
  showNotes?: boolean,
}
const renderClosingHeader = (status) => {
  let component = <div></div>
  if (status == 'closed') {
    component = <h5 className={styles.closedStatus}>{status.toUpperCase()}</h5>
  } else if (status == 'closing') {
    component = <h5 className={styles.closingStatus}>{status.toUpperCase()}</h5>
  } else if (status == 'suspended') {
    component = <h5 className={styles.closingStatus}>{status.toUpperCase()}</h5>
  }
  return component
}
const renderDeceasedHeader = (dod) => {
  if (dod) {
    return <h5 className={styles.deceasedFlag}>DECEASED</h5>
  } else {
    return <div></div>
  }
}

export const renderCheckingBalance = (checkingBalance) => {
  if (checkingBalance) {
    return formatters.money(checkingBalance)
  } else {
    return '$-.--'
  }
}

const Header = (props: Props) => {
  const {
    number,
    beneficiary,
    otherDistributions = { open: [], pending: [], paid: [], openTotal: 0, pendingTotal: 0, paidTotal: 0 },
    touchNav,
    checkingBalance,
    setCheckingBalance,
    showFeeSchedule,
    form,
    showNotes = false,
  } = props

  const dispatch = useDispatch()

  const [dod, setDod] = useState('')
  const [lastUpdate, setLastUpdate] = useState('')
  const [openDate, setOpenDate] = useState('')
  const [status, setStatus] = useState('')
  const [trustName, setTrustName] = useState('')
  const [productDescription, setProductDescription] = useState('')
  const [subaccountBalance, setSubaccountBalance] = useState('')
  const [feeSchedule, setFeeSchedule] = useState('')
  const [feeScheduleData, setFeeScheduleData] = useState([])
  const [notes, setNotes] = useState([])

  const handleNotesUpdate = (updatedNotes) => {
    setNotes(updatedNotes)
  }

  useEffect(() => {
    httpGet(`/admin/accounts/${number}?summary=true`)
      .then((data) => {
        setCheckingBalance(data.checkingBalance)
        setDod(data.dod)
        setLastUpdate(data.lastUpdate)
        setOpenDate(data.openDate)
        setStatus(data.status)
        setTrustName(data.trustName)
        setProductDescription(data.productDescription)
        setSubaccountBalance(data.subaccountBalance)
        setFeeSchedule(data.feeSchedule)
        setFeeScheduleData(data.feeScheduleData || [])
        setNotes(data.notes || [])
      })
      .catch((err) => null)
  }, [number])

  const isNewAccount = status === 'approved' && (openDate == null || openDate === '')

  return (
    <div className={`${styles.titleBox} ${touchNav && styles.titleHeaderHeight}`}>
      <div className={styles.titleBoxRow}>
        <div className={`${styles.nameWrapper}`}>
          <span className={styles.titleName}>
            <div className={styles.trusts}>
              <div className={styles.trustsHeader}>
                <div className={styles.accountNumberRow}>
                  <Link to={`/accounts/${number}`} className={styles.accountNumberLink} target="_blank" referrerPolicy="no-referrer">
                    <h5 className={styles.accountNumber}>{number}</h5>
                  </Link>
                  {isNewAccount && <h5 className={styles.newAccountBadge}>New Account</h5>}
                </div>
                <Link to={`/accounts/${number}`} className={styles.redirectIcon} target="_blank" referrerPolicy="no-referrer">
                  <IconRedirect h={16} w={16} />
                </Link>
              </div>
              {renderClosingHeader(status)}
            </div>
          </span>
          <div>
            <div className={styles.accountNumberLine}>
              <h1>
                <Link to={`/accounts/${number}`} className={styles.beneName} target="_blank" referrerPolicy="no-referrer">
                  {beneficiary}
                </Link>
              </h1>
              {dod && renderDeceasedHeader(dod)}
            </div>
            <div className={styles.trustName}>{trustName}</div>
            <div className={styles.productDescription}>{productDescription}</div>
          </div>
        </div>

        <div className={`${styles.balanceWrapper}`}>
          <div>
            <p className={styles.boxLabel}>Total Account Balance</p>
            <h2>{renderCheckingBalance(checkingBalance)}</h2>
          </div>
          {subaccountBalance != 0 && (
            <div className={styles.subAccount}>
              <p className={styles.boxLabel}>Subaccounts Balance</p>
              <h2>{renderCheckingBalance(subaccountBalance)}</h2>
            </div>
          )}
        </div>

        <div className={`${styles.accountStatusWrapper}`}>
          <div>
            <p className={styles.accountBoxLabel}>Account Information</p>
            <p className={styles.accountStatusCaption}>Last updated {formatters.date(lastUpdate)}</p>
          </div>
          <div>
            <p className={`${styles.accountBoxLabel} ${styles.accountBoxStatus}`}>{formatters.legibleAccountStatus(status)}</p>
            <p className={styles.accountStatusCaption}>Open {formatters.date(openDate)}</p>
          </div>
        </div>

        <div className={`${styles.accountStatusWrapper}`}>
          <div className={styles.rowHeader}>
            {Number(form.account.floorAmount) > 0 && <Row label="Floor" val={formatters.money(form.account.floorAmount)} singleLine />}
            {form.account.hasBudget && <Row label="Budget" val={<IconSwitch flag={form.account.hasBudget} />} singleLine />}
            {form.account.pCardLast4 && <Row label="P-Card Last 4" val={`${form.account.pCardLast4}`} singleLine />}
          </div>
        </div>
        <div className={`${styles.accountStatusWrapper}`} style={{ padding: '0 6px' }}>
          <AccordionStylingExpansion
            items={[
              showFeeSchedule && {
                title: 'FEE SCHEDULE',
                isEmpty: !feeSchedule,
                defaultExpanded: true,
                details: <FeeSchedule feeSchedule={feeSchedule} feeScheduleData={feeScheduleData} />,
              },
              {
                title: 'OPEN REQUESTS',
                isEmpty: otherDistributions.open.length === 0,
                details: <RequestInfo data={otherDistributions.open} total={otherDistributions.openTotal} totalType={'Open'} />,
              },
              {
                title: 'PENDING REQUESTS',
                isEmpty: otherDistributions.pending.length === 0,
                details: <RequestInfo data={otherDistributions.pending} total={otherDistributions.pendingTotal} totalType={'Pending'} />,
              },
              {
                title: 'PAID REQUESTS',
                isEmpty: otherDistributions.paid.length === 0,
                details: <RequestInfo data={otherDistributions.paid} total={otherDistributions.paidTotal} totalType={'Paid'} />,
              },
            ].filter((a) => a && !a.isEmpty)}
          />
        </div>
        {showNotes && <Notes accountNumber={number} dispatch={dispatch} notes={notes} type="account" onNotesUpdate={handleNotesUpdate} />}
      </div>
    </div>
  )
}

export default Header
